/**
 * 알림 센터 (탑바 벨): 인앱 알림함 폴링 + Web Push 구독.
 * - 보이는 탭에서만 30초 폴링. 인증 실패 시 중단, 일시 오류는 최대 5분까지 백오프.
 * - 항목 클릭 → 읽음 처리 후 링크 이동, 모두 읽음 버튼
 * - "브라우저 알림 켜기": push-permission.js 의 requestPush — 먼저 설명 시트, 거부된 기기에는 켜는 방법.
 *   iOS는 홈 화면 추가(PWA)에서만 동작하므로 안내 문구를 보여준다.
 */
import { openExclusive, closedExclusive } from './popover-registry.js';
import { syncAppBadge } from './pwa.js';
import { readPushState, requestPush } from './push-permission.js';
function csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]');
    if (meta && meta.content) return meta.content;
    const m = document.cookie.match(/(?:^|;\s*)_csrf=([^;]+)/);
    return m ? decodeURIComponent(m[1]) : '';
}

function api(path, opts) {
    opts = opts || {};
    opts.headers = Object.assign({ 'X-CSRF-Token': csrfToken() }, opts.headers || {});
    return fetch(path, opts).then((r) => {
        if (!r.ok) {
            const error = new Error('http ' + r.status);
            error.status = r.status;
            const retry = r.headers.get('Retry-After');
            error.retryAfter = retry ? (/^\d+$/.test(retry) ? Number(retry) * 1000 : Math.max(0, Date.parse(retry) - Date.now())) : 0;
            throw error;
        }
        return r.json();
    });
}

function timeAgo(iso) {
    const t = new Date(iso);
    const s = Math.floor((Date.now() - t.getTime()) / 1000);
    if (s < 60) return '방금';
    if (s < 3600) return Math.floor(s / 60) + '분 전';
    if (s < 86400) return Math.floor(s / 3600) + '시간 전';
    return String(t.getMonth() + 1).padStart(2, '0') + '-' + String(t.getDate()).padStart(2, '0');
}

// 푸시 구독 · 「전체」 시트 항목 · 권한 시트는 push-permission.js — 예전 가져오기 경로를 그대로 둔다.
export { subscribePush, setupPushQuick } from './push-permission.js';

export function setupNotificationCenter() {
    const bell = document.getElementById('notifBell');
    const menu = document.getElementById('notifMenu');
    if (!bell || !menu || bell.dataset.notificationReady) return;
    bell.dataset.notificationReady = '1';
    syncAppBadge(Number(bell.dataset.unread) || 0); // 첫 렌더의 서버 값

    const badge = document.getElementById('notifBadge');
    const list = document.getElementById('notifList');
    const markAll = document.getElementById('notifMarkAll');
    const pushBtn = document.getElementById('notifPushToggle');
    const pushHint = document.getElementById('notifPushHint');

    let lastUnread = -1;
    function setBadge(n) {
        // 새 알림 도착(증가)을 감지하면 벨을 흔들고 배지를 튕긴다.
        if (lastUnread >= 0 && n > lastUnread) {
            bell.classList.remove('notif-ring');
            void bell.offsetWidth; // 애니메이션 재시작
            bell.classList.add('notif-ring');
            if (badge) {
                badge.classList.remove('notif-pop');
                void badge.offsetWidth;
                badge.classList.add('notif-pop');
            }
        }
        lastUnread = n;
        return setBadgeInner(n);
    }
    function setBadgeInner(n) {
        syncAppBadge(n);
        if (!badge) return;
        badge.textContent = n > 99 ? '99+' : String(n);
        badge.style.display = n > 0 ? '' : 'none';
    }

    function render(items) {
        list.innerHTML = '';
        if (!items.length) {
            const empty = document.createElement('div');
            empty.className = 'text-muted';
            empty.style.cssText = 'padding:20px 14px; text-align:center;';
            empty.textContent = menu.dataset.empty;
            list.appendChild(empty);
            return;
        }
        items.forEach((n) => {
            const row = document.createElement('button');
            row.type = 'button';
            row.style.cssText = 'display:flex; gap:10px; align-items:flex-start; width:100%; text-align:left; font:inherit; color:inherit; background:none; border:none; border-bottom:1px solid var(--color-border, #f0f0f0); padding:10px 14px; cursor:pointer;'
                + (n.link_unavailable ? ' opacity:.45;' : n.is_read ? ' opacity:.62;' : '');
            // 숨긴 모듈로 가는 알림 (Y1): 서버가 링크를 비우고 표시한다 — 누르면
            // 읽음만 되고 이동하지 않는다. 왜 안 열리는지는 툴팁이 말한다.
            if (n.link_unavailable) {
                row.dataset.linkUnavailable = '1';
                if (menu.dataset.linkUnavailable) row.title = menu.dataset.linkUnavailable;
            }
            const dot = document.createElement('span');
            dot.style.cssText = 'flex-shrink:0; width:8px; height:8px; border-radius:50%; margin-top:6px; background:'
                + (n.is_read ? 'transparent' : 'var(--color-primary, #2563eb)') + ';';
            const main = document.createElement('span');
            main.style.cssText = 'display:flex; flex-direction:column; gap:2px; min-width:0;';
            const title = document.createElement('strong');
            title.style.cssText = 'font-size:13px;';
            title.textContent = n.title;
            const body = document.createElement('span');
            body.style.cssText = 'font-size:12px; color:var(--color-text-secondary, #666); overflow:hidden; text-overflow:ellipsis;';
            body.textContent = n.body;
            const when = document.createElement('span');
            when.style.cssText = 'font-size:11px; color:var(--color-text-secondary, #999);';
            when.textContent = timeAgo(n.created_at);
            main.appendChild(title);
            if (n.body) main.appendChild(body);
            main.appendChild(when);
            row.appendChild(dot);
            row.appendChild(main);
            row.addEventListener('click', () => {
                row.disabled = true;
                api('/api/notifications/' + n.id + '/read', { method: 'POST' })
                    .then(() => { cached = null; nextAt = 0; if (n.link) window.location.href = n.link; else refresh(true); })
                    .catch(showError).finally(() => { row.disabled = false; });
            });
            list.appendChild(row);
        });
    }

    const PERIOD = 30000;
    let timer, inFlight = null, cached = null, nextAt = 0, failures = 0, stopped = false;
    let wantsList = false;
    const active = () => document.visibilityState === 'visible' && navigator.onLine !== false;
    function schedule() {
        clearTimeout(timer);
        if (!stopped && active()) timer = setTimeout(() => refresh(menu.classList.contains('open')), Math.max(0, nextAt - Date.now()));
    }
    function showError(error) {
        const auth = error.status === 401 || error.status === 403;
        if (auth) { stopped = true; cached = null; clearTimeout(timer); setBadge(0); }
        if (!wantsList && !menu.classList.contains('open')) return;
        list.replaceChildren();
        const message = document.createElement('div');
        message.style.cssText = 'padding:20px 14px; text-align:center;';
        message.setAttribute('role', 'status');
        message.textContent = auth ? menu.dataset.sessionExpired : menu.dataset.failed;
        if (auth) {
            const link = document.createElement('a');
            link.href = '/login'; link.textContent = menu.dataset.login;
            link.style.display = 'block'; message.appendChild(link);
        }
        list.appendChild(message);
    }
    function refresh(withList) {
        wantsList = wantsList || withList;
        if (stopped) { if (withList) showError({ status: 401 }); return Promise.resolve(); }
        if (inFlight) return inFlight;
        if (cached && withList) render(cached.items);
        if (!active() || Date.now() < nextAt) { schedule(); return Promise.resolve(); }
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 15000);
        inFlight = api('/api/notifications', { signal: controller.signal })
            .then((d) => {
                cached = d; failures = 0; nextAt = Date.now() + PERIOD;
                setBadge(d.unread);
                if (wantsList) render(d.items);
            })
            .catch((error) => {
                failures += 1;
                nextAt = Date.now() + Math.max(Math.min(PERIOD * 2 ** Math.min(failures, 4), 300000), error.retryAfter || 0);
                showError(error);
            })
            .finally(() => { clearTimeout(timeout); inFlight = null; wantsList = false; schedule(); });
        return inFlight;
    }

    function openMenu() {
        menu.style.display = 'block';
        bell.setAttribute('aria-expanded', 'true');
        requestAnimationFrame(() => menu.classList.add('open'));
        // 다른 팝오버(사용자 메뉴·언어 메뉴·앱 메뉴)를 닫고, 바깥 클릭·Esc 는 공용
        // 등록부의 캡처 리스너에 맡긴다 — 트리거들의 stopPropagation 에 막히지 않는다.
        openExclusive('notification-center', closeMenu, { roots: [menu, bell] });
        refresh(true);
        syncPushButton();
    }
    function closeMenu() {
        closedExclusive('notification-center');
        menu.classList.remove('open');
        bell.setAttribute('aria-expanded', 'false');
        setTimeout(() => { if (!menu.classList.contains('open')) menu.style.display = 'none'; }, 160);
    }
    bell.addEventListener('click', (e) => {
        e.stopPropagation();
        if (menu.style.display !== 'none' && menu.classList.contains('open')) closeMenu();
        else openMenu();
    });
    markAll.addEventListener('click', () => {
        if (stopped || markAll.disabled) return;
        markAll.disabled = true;
        api('/api/notifications/read-all', { method: 'POST' })
            .then(async () => { if (inFlight) await inFlight; cached = null; nextAt = 0; return refresh(true); })
            .catch(showError).finally(() => { markAll.disabled = false; });
    });

    // 최초 배지는 서버 렌더링 값을 사용한다. 탭 복귀도 같은 캐시/백오프를 따른다.
    if (active()) timer = setTimeout(() => refresh(false), PERIOD);
    document.addEventListener('visibilitychange', () => {
        if (active()) refresh(menu.classList.contains('open')); else clearTimeout(timer);
    });
    window.addEventListener('online', () => refresh(menu.classList.contains('open')));
    window.addEventListener('offline', () => clearTimeout(timer));

    // 알림 수신 설정(CRM/ERP 종류별 토글)은 /profile 의 '알림 수신' 카드에 있다
    // (partials/profile.html → POST /api/prefs notify.crm|notify.erp). 벨 메뉴에는 두지 않는다.

    // ── Web Push ── (상태 · 권한 시트는 push-permission.js)
    function showHint(text) {
        pushHint.textContent = text || '';
        pushHint.style.display = text ? 'block' : 'none';
    }

    // 거부된 기기에서도 단추는 살아 있다 — 누르면 그 기기에서 켜는 곳을 보인다(브라우저는 다시 묻지 않는다).
    function syncPushButton() {
        if (!pushBtn) return;
        readPushState().then((state) => {
            pushBtn.style.display = state === 'unsupported' ? 'none' : '';
            pushBtn.disabled = state === 'ios-install' || state === 'on';
            pushBtn.textContent = state === 'on' ? menu.dataset.pushOn + ' ✓'
                : state === 'blocked' ? (menu.dataset.pushHelp || menu.dataset.enablePush)
                    : menu.dataset.enablePush;
            pushBtn.dataset.pushState = state;
            showHint(state === 'ios-install' ? menu.dataset.iosHint : state === 'blocked' ? menu.dataset.pushDenied : '');
        });
    }
    document.addEventListener('push:changed', syncPushButton);

    if (pushBtn) {
        // 시트가 열리면 벨 메뉴는 닫힌다(공용 팝오버 등록부) — 닫힌 뒤 포커스는 벨로 돌아간다.
        pushBtn.addEventListener('click', () => { requestPush(bell); });
    }
}
